import React, { useMemo, useState } from 'react';
import { ArrowUpDownIcon, SearchIcon, UserPlusIcon, UsersIcon } from 'lucide-react';
import { useData } from '../../../store/DataContext';
import { useHrPayroll } from '../../../contexts/HrPayrollContext';
import { HrEmployee } from '../../../types/hr';
import { employeeName, formatKwacha } from '../../../utils/hrFormat';
import { Button } from '../../../components/ui/Button';
import { EmployeeForm } from './EmployeeForm';
import { EmployeeProfile } from './EmployeeProfile';
import { EmployeeStatusBadge } from './EmployeeStatusBadge';
type SortKey = 'employeeNumber' | 'name' | 'department' | 'basicSalary';
export function EmployeeManagement({
  profileId,
  onOpenProfile
}: {
  profileId: string | null;
  onOpenProfile: (id: string | null) => void;
}) {
  const {
    users
  } = useData();
  const {
    employees,
    departments,
    positions
  } = useHrPayroll();
  const [query, setQuery] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [status, setStatus] = useState('current');
  const [sort, setSort] = useState<{
    key: SortKey;
    dir: 1 | -1;
  }>({
    key: 'employeeNumber',
    dir: 1
  });
  const [registering, setRegistering] = useState(false);
  const deptName = (id: string) => departments.find(d => d.id === id)?.name ?? '—';
  const positionTitle = (id: string) => positions.find(p => p.id === id)?.title ?? '—';
  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return employees.filter(e => status === 'current' ? e.status !== 'Terminated' && e.status !== 'Inactive' : status === 'all' ? true : e.status === status).filter(e => !departmentId || e.departmentId === departmentId).filter(e => !q || employeeName(e).toLowerCase().includes(q) || e.employeeNumber.toLowerCase().includes(q) || (e.idNumber ?? '').toLowerCase().includes(q) || deptName(e.departmentId).toLowerCase().includes(q) || positionTitle(e.positionId).toLowerCase().includes(q)).sort((a, b) => {
      const value = (e: HrEmployee) => sort.key === 'name' ? `${e.surname} ${e.firstName}` : sort.key === 'department' ? deptName(e.departmentId) : sort.key === 'basicSalary' ? e.basicSalary : e.employeeNumber;
      const av = value(a);
      const bv = value(b);
      return (typeof av === 'number' ? av - (bv as number) : String(av).localeCompare(String(bv))) * sort.dir;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employees, query, departmentId, status, sort, departments, positions]);
  const profile = employees.find(e => e.id === profileId);
  if (profile) return <EmployeeProfile employee={profile} onBack={() => onOpenProfile(null)} data-id="element-505" data-mp="10wio7akmeqks" />;
  const header = (key: SortKey, label: string, right = false) => <th scope="col" className={`px-4 py-3 ${right ? 'text-right' : ''}`} aria-sort={sort.key === key ? sort.dir === 1 ? 'ascending' : 'descending' : 'none'} data-id="element-506" data-mp="1aw14nh1bnu83">
      <button type="button" onClick={() => setSort(s => ({
      key,
      dir: s.key === key ? s.dir === 1 ? -1 : 1 : 1
    }))} className="inline-flex items-center gap-1 uppercase hover:text-slate-900" data-id="element-507" data-mp="15yhqsi8jdjgo">
        {label} <ArrowUpDownIcon className="h-3 w-3" aria-hidden="true" data-id="element-508" data-mp="1tjs647gvkxk5" />
      </button>
    </th>;
  return <div className="space-y-4" data-id="element-509" data-mp="1fkam05eiy8h4">
      <div className="flex flex-wrap items-end gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-id="element-510" data-mp="1kjn64ie0h1g0">
        <div className="relative min-w-[240px] flex-1" data-id="element-511" data-mp="15u7jneliji64">
          <label htmlFor="emp-search" className="sr-only" data-id="element-512" data-mp="15tfw8r22kqqx">Search employees</label>
          <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" data-id="element-513" data-mp="1an0o01d2koho" />
          <input id="emp-search" type="search" value={query} onChange={e => setQuery(e.target.value)} placeholder="Name, employee ID, NRC, department or position" className="w-full rounded-md border border-slate-300 py-2 pl-9 pr-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" data-id="element-514" data-mp="11t5hoq07os0g" />
        </div>
        <label className="text-sm" data-id="element-515" data-mp="1v65728bn196r">
          <span className="sr-only" data-id="element-516" data-mp="1v52i45xvlgth">Department</span>
          <select value={departmentId} onChange={e => setDepartmentId(e.target.value)} className="rounded-md border border-slate-300 bg-white p-2 text-sm" data-id="element-517" data-mp="1q694mdil9ptb">
            <option value="" data-id="element-518" data-mp="1ldzf2yldyrrk">All departments</option>
            {departments.map(d => <option key={d.id} value={d.id} data-id="element-519" data-mp="1ggg3k6g2ot3b">{d.name}</option>)}
          </select>
        </label>
        <label className="text-sm" data-id="element-520" data-mp="1bjfxhvj83qc7">
          <span className="sr-only" data-id="element-521" data-mp="1bfjrv6phc2nc">Status</span>
          <select value={status} onChange={e => setStatus(e.target.value)} className="rounded-md border border-slate-300 bg-white p-2 text-sm" data-id="element-522" data-mp="17f895zgogcei">
            <option value="current" data-id="element-523" data-mp="12j17wjz87p2w">Current staff</option>
            <option value="Active" data-id="element-524" data-mp="xjev2bnb8aum">Active</option>
            <option value="Suspended" data-id="element-525" data-mp="2wv7j3is4e5v7">Suspended</option>
            <option value="Inactive" data-id="element-526" data-mp="2wupgm4ev6veu">Inactive</option>
            <option value="Terminated" data-id="element-527" data-mp="2rwe9i1fyez0b">Terminated</option>
            <option value="all" data-id="element-528" data-mp="2rwxmr4unsz80">All records</option>
          </select>
        </label>
        <Button onClick={() => setRegistering(true)} data-id="element-529" data-mp="2zf49518yrfmf">
          <UserPlusIcon className="mr-1.5 h-4 w-4" aria-hidden="true" data-id="element-530" data-mp="34egshc0mm4k9" /> Register employee
        </Button>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm" data-id="element-531" data-mp="39572x3nwsxlt">
        {rows.length === 0 ? <div className="py-16 text-center text-slate-500" data-id="element-532" data-mp="3e40gsx73yqmi">
            <UsersIcon className="mx-auto mb-2 h-10 w-10 text-slate-300" aria-hidden="true" data-id="element-533" data-mp="2ghkxdpv4fiwb" />
            <p className="text-sm" data-id="element-534" data-mp="2lfcpajvrn50i">{employees.length === 0 ? 'No employees registered yet. Register your first employee or link an existing staff account.' : 'No employees match these filters.'}</p>
          </div> : <div className="overflow-x-auto" data-id="element-535" data-mp="2vettewvddaud">
            <table className="min-w-full text-sm" data-id="element-536" data-mp="2vbpjrjz4xmwb">
              <thead className="bg-slate-50 text-left text-xs font-semibold tracking-wide text-slate-500" data-id="element-537" data-mp="5nyjifdj9poz">
                <tr data-id="element-538" data-mp="am9tqr7qq27q">
                  {header('employeeNumber', 'Employee ID')}
                  {header('name', 'Name')}
                  {header('department', 'Department')}
                  <th scope="col" className="px-4 py-3 uppercase" data-id="element-539" data-mp="21qtjs43v3srq">Position</th>
                  <th scope="col" className="px-4 py-3 uppercase" data-id="element-540" data-mp="2fs604gmxpwe5">System account</th>
                  {header('basicSalary', 'Basic salary', true)}
                  <th scope="col" className="px-4 py-3 uppercase" data-id="element-541" data-mp="2bqu5fwp7ekz3">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-542" data-mp="1ny3wlwclkht1">
                {rows.map(e => <tr key={e.id} className="hover:bg-slate-50" data-id="element-543" data-mp="1izk8pqeg0vy1">
                    <td className="whitespace-nowrap px-4 py-3 font-mono text-blue-700" data-id="element-544" data-mp="1wtlakt0oyz2m">{e.employeeNumber}</td>
                    <td className="whitespace-nowrap px-4 py-3" data-id="element-545" data-mp="1s0iby6h24zfk">
                      <button type="button" onClick={() => onOpenProfile(e.id)} className="flex items-center gap-2.5 text-left font-medium text-slate-900 hover:text-blue-700 hover:underline" data-id="element-546" data-mp="14bpe24gvkhib">
                        {e.photo ? <img src={e.photo} alt="" className="h-8 w-8 rounded-full object-cover" data-id="element-547" data-mp="q5xjw1pz21lm" /> : <span className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold text-slate-600" data-id="element-548" data-mp="uxfbhz63uptx">{e.firstName[0]}{e.surname[0]}</span>}
                        {employeeName(e)}
                      </button>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-slate-700" data-id="element-549" data-mp="l5jd0n03wxp6">{deptName(e.departmentId)}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-slate-700" data-id="element-550" data-mp="1dwjil2e369iy">{positionTitle(e.positionId)}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-slate-600" data-id="element-551" data-mp="yzsdcchqe3lf">{e.userId ? users.find(u => u.id === e.userId)?.role ?? 'Linked' : '—'}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-right tabular-nums" data-id="element-552" data-mp="140gevs3l62tw">{formatKwacha(e.basicSalary)}</td>
                    <td className="whitespace-nowrap px-4 py-3" data-id="element-553" data-mp="3ookzkglw07p9"><EmployeeStatusBadge status={e.status} data-id="element-554" data-mp="3opnqupn4gc5p" /></td>
                  </tr>)}
              </tbody>
            </table>
            <p className="border-t border-slate-100 px-4 py-2 text-xs text-slate-500" data-id="element-555" data-mp="35vp99vh0mpkf">{rows.length} of {employees.length} employees</p>
          </div>}
      </div>

      {registering && <EmployeeForm onClose={() => setRegistering(false)} onSaved={id => {
      setRegistering(false);
      onOpenProfile(id);
    }} data-id="element-556" data-mp="2w1ymcv0jen6u" />}
    </div>;
}